import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { FiX, FiLoader, FiImage, FiRefreshCw, FiInfo, FiAlertTriangle } from 'react-icons/fi';

/**
 * ContentRefineModal - V3 Refine Flow
 * 
 * Simple modal for refining content images.
 * Uses the V3 refine endpoint which:
 * 1. Analyzes the generated image (Gemini Flash)
 * 2. Parses user instruction (GPT-5.2)
 * 3. Validates feasibility
 * 4. Generates refined image (Gemini Nano Banana with reference)
 * 
 * Props:
 * - content: The content object with image(s)
 * - imageIndex: Which image in the stack to refine (default: 0)
 * - imageUrl: Direct URL of the image to refine (used when refining from lightbox)
 * - onClose: Callback when modal closes
 * - onSuccess: Callback when refine succeeds
 */
function ContentRefineModal({ content, imageIndex = 0, imageUrl = null, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refinementNote, setRefinementNote] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [notesToUser, setNotesToUser] = useState([]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Quick refinement suggestions
  const quickSuggestions = [
    { label: 'Add people', value: 'Add some people to the scene' },
    { label: 'Evening time', value: 'Make it look like evening with warm lights' },
    { label: 'More cozy', value: 'Make the atmosphere more cozy and inviting' },
    { label: 'Brighter', value: 'Make the image brighter and more vibrant' },
    { label: 'More minimal', value: 'Make the scene cleaner and more minimal' },
    { label: 'Different weather', value: 'Change to a cloudy or rainy atmosphere' },
  ];

  const handleQuickSuggestion = (suggestion) => {
    setRefinementNote(prev => {
      if (prev.trim()) {
        return `${prev}. ${suggestion}`;
      }
      return suggestion;
    });
  };

  // Convert image URL to base64
  const imageUrlToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Failed to convert image to base64:', err);
      return null;
    }
  };

  // Determine which image URL to use for refinement
  const getImageToRefine = () => {
    // If explicit imageUrl passed (from lightbox), use that
    if (imageUrl) return imageUrl;
    // If content has images array and valid index, use that
    if (content.images && content.images.length > imageIndex) {
      return content.images[imageIndex];
    }
    // Fallback to main image_url
    return content.image_url;
  };

  // Main refine handler - uses V3 API
  const handleRefine = async () => {
    if (!refinementNote.trim()) {
      setError('Please provide refinement instructions');
      return;
    }

    const imageToRefine = getImageToRefine();
    if (!imageToRefine) {
      setError('No image available to refine.');
      return;
    }

    setLoading(true);
    setAnalyzing(true);
    setError('');
    setNotesToUser([]);

    try {
      // Convert current image to base64
      const imageBase64 = await imageUrlToBase64(imageToRefine);
      if (!imageBase64) {
        throw new Error('Failed to load current image');
      }

      setAnalyzing(false);

      // Call V3 refine endpoint with image_index
      const response = await api.refineContent({
        generated_image_base64: imageBase64,
        user_instruction: refinementNote,
        content_id: content.id,
        client_id: content.client_id,
        campaign_id: content.campaign_id,
        image_index: imageIndex,  // Tell backend which image in stack we're refining
        ratio: '1:1',
        quality: 'standard'
      });

      if (response.success) {
        // Show notes from the refine interpreter
        if (response.notes && response.notes.length > 0) {
          setNotesToUser(response.notes);
        }
        
        // Show interpretation summary
        if (response.interpretation && response.interpretation.intent) {
          setNotesToUser(prev => [...prev, `Applied: ${response.interpretation.intent}`]);
        }

        onSuccess(response, imageIndex);  // Pass imageIndex so parent knows which image was refined
      } else {
        throw new Error(response.error || 'Refinement failed');
      }
    } catch (err) {
      console.error('Refinement error:', err);
      
      // Check if this is a timeout/proxy error (502, timeout, network error)
      const isTimeoutError = 
        err.response?.status === 502 ||
        err.response?.status === 504 ||
        err.code === 'ECONNABORTED' ||
        err.message?.toLowerCase().includes('timeout') ||
        err.message?.toLowerCase().includes('network');
      
      if (isTimeoutError && content.id) {
        // Poll for updated content - backend might have completed the refine
        console.log('Detected timeout/proxy error, polling for refined content...');
        setError('Processing taking longer than expected. Checking for results...');
        
        const pollDelays = [3000, 5000, 8000, 12000, 15000];
        const originalVersion = content.version || 1;
        
        for (let i = 0; i < pollDelays.length; i++) {
          try {
            console.log(`Poll attempt ${i + 1}/${pollDelays.length}, waiting ${pollDelays[i]/1000}s...`);
            await new Promise(resolve => setTimeout(resolve, pollDelays[i]));
            
            // Fetch the content images to check if version incremented
            const imageData = await api.getContentImage(content.id, false, true);
            
            // Check if we have images and if the image at our index changed
            if (imageData && imageData.images && imageData.images[imageIndex]) {
              const currentImageUrl = content.images?.[imageIndex] || content.image_url;
              const newImageUrl = imageData.images[imageIndex];
              
              // If image URL changed, refinement succeeded
              if (newImageUrl && newImageUrl !== currentImageUrl) {
                console.log('Found refined content via polling!');
                setError('');
                setNotesToUser(['Refinement completed successfully']);
                onSuccess({ success: true, refined_image: newImageUrl }, imageIndex);
                return;
              }
            }
          } catch (pollErr) {
            console.log(`Poll attempt ${i + 1} failed:`, pollErr.message);
          }
        }
        
        // After all polls, still no update
        setError('Refinement is taking longer than usual. Please refresh the page to check if it completed.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to refine content. Please try again.');
      }
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl w-full max-w-2xl shadow-2xl border border-border/50 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-border/50">
          <div>
            <h2 className="text-xl font-bold text-foreground">Refine Content</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {content.platform} · {content.content_type?.replace(/_/g, ' ')}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Version indicator */}
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Version</p>
              <p className="text-sm font-medium text-foreground">
                {content.version || 1} / 6
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
              data-testid="close-refine-modal"
            >
              <FiX size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Version limit warning */}
          {(content.version || 1) >= 6 && (
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm flex items-center gap-2">
              <FiAlertTriangle size={16} />
              <span>Maximum 6 versions reached. View Version History to restore a previous version.</span>
            </div>
          )}

          {/* Current Image Preview */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              {content.images && content.images.length > 1 
                ? `Refining Image ${imageIndex + 1} of ${content.images.length}`
                : 'Current Image'
              }
            </h3>
            <div className="bg-secondary rounded-lg p-4 border border-border">
              <div className="flex gap-4">
                {getImageToRefine() && (
                  <img 
                    src={getImageToRefine()} 
                    alt="" 
                    className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Describe how you want to change this image.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <FiInfo className="inline mr-1" />
                    You can change time of day, weather, add/remove people, adjust mood - 
                    but you can&apos;t change the camera angle or show things not in the photo.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Refinement Input */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-foreground mb-2">
              What would you like to change?
            </label>
            <textarea
              value={refinementNote}
              onChange={(e) => setRefinementNote(e.target.value)}
              placeholder="e.g., Make it a cloudy evening with people hanging out..."
              className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              rows={3}
              data-testid="refinement-note-input"
            />
          </div>

          {/* Quick Suggestions */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-2">Quick suggestions:</p>
            <div className="flex flex-wrap gap-2">
              {quickSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickSuggestion(suggestion.value)}
                  className="px-3 py-1.5 text-xs bg-secondary hover:bg-secondary/80 text-foreground rounded-full border border-border transition-colors"
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes/Feedback from AI */}
          {notesToUser.length > 0 && (
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs font-medium text-blue-400 mb-2">AI Notes:</p>
              <ul className="text-xs text-blue-300 space-y-1">
                {notesToUser.map((note, idx) => (
                  <li key={idx}>• {note}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Loading State */}
          {analyzing && (
            <div className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <FiLoader className="animate-spin text-primary" size={20} />
                <div>
                  <p className="text-sm font-medium text-primary">Analyzing image...</p>
                  <p className="text-xs text-muted-foreground">Extracting visual elements to guide refinement</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-border">
          <button 
            onClick={onClose} 
            className="px-6 py-2 text-foreground hover:text-foreground/80"
          >
            Cancel
          </button>
          
          <button
            onClick={handleRefine}
            disabled={!refinementNote.trim() || loading || (content.version || 1) >= 6}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium"
            data-testid="refine-submit-btn"
          >
            {loading ? (
              <>
                <FiLoader className="animate-spin" />
                <span>{analyzing ? 'Analyzing...' : 'Refining...'}</span>
              </>
            ) : (
              <>
                <FiRefreshCw />
                <span>Refine Image</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ContentRefineModal;
